import { XMarkIcon } from '@heroicons/react/16/solid';
import { PlayCircleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import { resolveAccountHealthVariant } from 'src/alerts/AlertRegistry';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';

// Explainer video shown on count-based spam variants, adopted verbatim from
// `AccountHealthBanner` (the copy authority until RET-1879 deletes it): plays
// inside an in-app modal (YouTube embed) so the user never leaves Leadsales.
const SPAM_EXPLAINER_VIDEO_ID = 'D3ZuckNrngE';
const SPAM_EXPLAINER_VIDEO_EMBED = `https://www.youtube.com/embed/${SPAM_EXPLAINER_VIDEO_ID}?autoplay=1&rel=0`;
const SPAM_EXPLAINER_VIDEO_WATCH = `https://www.youtube.com/watch?v=${SPAM_EXPLAINER_VIDEO_ID}`;

/**
 * Per-variant rich extras beyond the alert's own title/body/CTA: the policy
 * link woven into the message (`<0>` markup in the body copy) and whether the
 * spam explainer video applies. Mirrors the legacy `BANNER_STYLES` table —
 * violation variants carry no message link (their external door is the CTA).
 */
const STATUS_DETAIL_EXTRAS: Record<
  string,
  { linkHref?: string; hasVideo?: boolean }
> = {
  'status.low_spamming': {
    linkHref: 'https://faq.whatsapp.com/933578044281252/?locale=es_LA',
    hasVideo: true,
  },
  'status.high_spamming': { hasVideo: true },
  'status.restricted': { linkHref: 'https://www.facebook.com/business/help' },
};

// The video chrome strings live in the legacy namespace on purpose: they are
// shared with the still-mounted banner, and copy moves only when the banner
// dies (RET-1879), same rule as the U8 consolidation.
const VIDEO_NS = 'whatsappApi/whatsappApi';

/**
 * Rich body for the account-health alert (U4/RET-1878): the full message with
 * its inline policy link, and the spam explainer video for the count-based
 * variants — adopted from `AccountHealthBanner`.
 *
 * Reads the live WhatsappApi context and derives the variant through the SAME
 * resolver the registry's copy uses, so body and detail can never show two
 * different violations. The count-based/violation distinction also mirrors
 * the provider: a violation type is only meaningful under HIGH_SPAMMING.
 */
export default function AccountHealthDetail(_props: tAlertDetailRendererProps) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const { t: tVideo } = useTranslation(VIDEO_NS);
  const { accountHealthStatus, violationType } = useWhatsappApiContext();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const variant = resolveAccountHealthVariant(
    accountHealthStatus ?? null,
    accountHealthStatus === 'HIGH_SPAMMING' ? (violationType ?? null) : null,
  );
  const extras = STATUS_DETAIL_EXTRAS[variant] ?? {};

  return (
    <div className="AlertDetail" data-testid="alert-detail-account-health">
      <p className="AlertDetail__Body">
        {extras.linkHref ? (
          <Trans
            t={t}
            i18nKey={`accountHealth.${variant}.body`}
            components={[
              <a
                key="0"
                href={extras.linkHref}
                target="_blank"
                rel="noopener noreferrer"
                className="AlertDetail__InlineLink"
                data-testid="alert-detail-health-link"
              />,
            ]}
          />
        ) : (
          t(`accountHealth.${variant}.body`)
        )}
      </p>

      {extras.hasVideo ? (
        <button
          type="button"
          onClick={() => setIsVideoOpen(true)}
          className="AlertDetail__VideoButton"
          data-testid="alert-detail-health-video"
        >
          <PlayCircleIcon className="w-4 h-4" aria-hidden="true" />
          {tVideo('page.general.account_health.watch_video')}
        </button>
      ) : null}

      {extras.hasVideo ? (
        <Modal.Legacy
          open={isVideoOpen}
          onClickBackdrop={() => setIsVideoOpen(false)}
          className="max-w-3xl p-0 overflow-hidden bg-black"
        >
          <button
            type="button"
            aria-label={tVideo('page.general.account_health.close_video')}
            onClick={() => setIsVideoOpen(false)}
            className="absolute top-2 right-2 z-10 rounded-full bg-black/60 p-1 text-white transition-colors hover:bg-black/80"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          {/* Mount the iframe only while open so the video autoplays on open
              and stops (unmounts) on close. */}
          {isVideoOpen && (
            <div className="aspect-video w-full">
              <iframe
                className="w-full h-full"
                src={SPAM_EXPLAINER_VIDEO_EMBED}
                title={t(`accountHealth.${variant}.title`)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {/* Fallback for users whose browser/extension blocks the embed. */}
          <p className="bg-black px-4 py-2 text-center text-xs text-white/70">
            <Trans
              t={tVideo}
              i18nKey="page.general.account_health.video_fallback"
              components={[
                <a
                  key="0"
                  href={SPAM_EXPLAINER_VIDEO_WATCH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-white underline"
                />,
              ]}
            />
          </p>
        </Modal.Legacy>
      ) : null}
    </div>
  );
}
